import { useState } from 'react'

import { Box, Text } from '@factorialco/deprecated-design-system'
import { ReconciliationBankJournalLinesDataTableDocument } from 'generated/resources/datatable.graphql'
import {
  FinanceValueObjectsJournalEntryTypeEnum,
  JournalLineReconciliationDocument,
  SnoozeJournalLineDocument,
  UnsnoozeJournalLineDocument,
} from 'generated/resources/graphql'

import Sidepanel from 'modules/core/components/Sidepanel'
import { useUnsafeCallback } from 'modules/core/hooks/useCallback'
import { useMutation } from 'modules/core/hooks/useMutation'
import { useQuery } from 'modules/core/hooks/useQuery'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import { ReconciliationForm } from 'modules/finance/components/ReconciliationForm'
import type { SourceReconciliationType } from 'modules/finance/components/ReconciliationForm/types'
import { ReconciliationBankJournalLinesDataTable } from 'modules/finance/components/ReconciliationFromBankAccountSidepanel/ReconciliationBankJournalLinesDatatable'

import { ReconciliationNotFound } from './ReconciliationNotFound'

interface Props {
  sourceType: SourceReconciliationType
  sourceId: number
  canReconcile: boolean
  currency: string
  title: string
  subtitle: string
  reconcilableJournalLineTitle: string
  summaryTitle: string
  summarySubtitle: string
  onCloseSidePanel: () => void
}

export const ReconciliationSidePanel = ({
  sourceType,
  sourceId,
  canReconcile,
  currency,
  title,
  subtitle,
  reconcilableJournalLineTitle,
  summaryTitle,
  summarySubtitle,
  onCloseSidePanel,
}: Props) => {
  const [selectedJournalLineId, setSelectedJournalLine] = useState<number | null>(null)

  const [snoozeJournalLine] = useMutation(SnoozeJournalLineDocument, {
    extractorFn: (d) => d.financeMutations.snoozeFinanceJournalLine,
  })

  const snooze = useUnsafeCallback(
    async (journalLineId: number, until: Date) => {
      const response = await snoozeJournalLine({
        variables: {
          id: journalLineId,
          snoozedUntil: until.toISOString(),
        },
        refetchQueries: [ReconciliationBankJournalLinesDataTableDocument],
      })
      const result = handleResult(response, () => undefined)
      if (result.success) {
        app.setFlash({
          type: 'correct',
          message: i18n.t('finance.reconciliations.submit_messages.journal_line_snoozed'),
        })
      } else {
        app.setFlash({
          type: 'wrong',
          message: i18n.t('finance.reconciliations.submit_messages.journal_line_snoozed_error'),
        })
      }
    },
    [snoozeJournalLine]
  )

  const [unsnoozeJournalLine] = useMutation(UnsnoozeJournalLineDocument, {
    extractorFn: (d) => d.financeMutations.unsnoozeFinanceJournalLine,
  })

  const unsnooze = useUnsafeCallback(
    async (journalLineId: number) => {
      const response = await unsnoozeJournalLine({
        variables: {
          id: journalLineId,
        },
        refetchQueries: [ReconciliationBankJournalLinesDataTableDocument],
      })
      const result = handleResult(response, () => undefined)
      if (result.success) {
        app.setFlash({
          type: 'correct',
          message: i18n.t('finance.reconciliations.submit_messages.journal_line_unsnoozed'),
        })
      } else {
        app.setFlash({
          type: 'wrong',
          message: i18n.t('finance.reconciliations.submit_messages.journal_line_unsnoozed_error'),
        })
      }
    },
    [unsnoozeJournalLine]
  )

  const { data: journalLineData, loading } = useQuery(JournalLineReconciliationDocument, {
    variables: { journalLineId: Number(selectedJournalLineId) },
    skip: !selectedJournalLineId,
  })

  const journalLine = journalLineData?.finance.journalLine ?? undefined

  const excludingJournalEntryTypes: FinanceValueObjectsJournalEntryTypeEnum[] | undefined =
    sourceType === 'bankAccount' ? ['bank', 'reconciliation'] : undefined

  const allowedJournalEntryTypes: FinanceValueObjectsJournalEntryTypeEnum[] | undefined =
    sourceType === 'financialDocument' ? ['bank'] : undefined

  if (!canReconcile) {
    return <ReconciliationNotFound onClose={onCloseSidePanel} />
  }

  return (
    <Sidepanel
      title={i18n.t('finance.reconciliations.sidepanel_title')}
      width="extended"
      open
      onClose={onCloseSidePanel}
    >
      <Box
        flexDirection="row"
        style={{
          marginTop: '-24px',
          marginBottom: '-24px',
          marginRight: '-24px',
          height: 'calc(100vh - 65px)',
          width: 'calc(100% + 24px)',
        }}
      >
        <Box
          gap="s24"
          padding="s16"
          paddingLeft="s0"
          overflowY="auto"
          border={{ right: { color: 'grey400', width: 's1', style: 'solid' } }}
          style={{ scrollbarGutter: 'auto', width: '50%' }}
        >
          <Box flexDirection="row" justifyContent="spaceBetween">
            <Box gap="s16">
              <Text.Normal weight="semibold">{title}</Text.Normal>
              <Text.Normal weight="normal">{subtitle}</Text.Normal>
            </Box>
          </Box>

          {sourceType === 'bankAccount' && (
            <ReconciliationBankJournalLinesDataTable
              selectedJournalLineId={selectedJournalLineId}
              onSelect={setSelectedJournalLine}
              onSnooze={snooze}
              onUnsnooze={unsnooze}
              bankAccountId={sourceId}
              currency={currency}
            />
          )}
        </Box>
        <Box style={{ width: '50%' }} gap="s16" paddingTop="s16">
          <ReconciliationForm
            sourceType={sourceType}
            journalLine={journalLine}
            loading={loading}
            currency={currency}
            onJournalLineFullyReconciled={() => setSelectedJournalLine(null)}
            excludingJournalEntryTypes={excludingJournalEntryTypes}
            allowedJournalEntryTypes={allowedJournalEntryTypes}
            onlyReconcilableJournalLines
            reconcilableJournalLineTitle={reconcilableJournalLineTitle}
            summaryTitle={summaryTitle}
            summarySubtitle={summarySubtitle}
          />
        </Box>
      </Box>
    </Sidepanel>
  )
}
